import { EventEmitter } from 'events';
import { Box3, Layers, Object3D, Sphere } from 'three';

import {
  navMeshDistanceFromObject,
  NavMeshQuery,
  RecastNavMesh,
} from '@spazious/dollhouse-navmesh';

import { DOLLHOUSE_LAYER_INTERACTIVE } from '../../constants/index.js';
import { Collider } from '../Collider/index.js';
import { ThreeOctree } from '../ThreeOctree.js';

import '../../shims/navmesh.js';

export type WorldEvents = {
  ready: [];
  update: [];
};

/**
 * World abstraction for spatial queries. It is composed of a navigation mesh and a collider.
 *
 * The navigation mesh is a mesh that is used to compute.
 */
export class World extends EventEmitter<WorldEvents> {
  navMeshFloorOffset: number = 0;
  objects: Set<Object3D> = new Set();
  octree: ThreeOctree = new ThreeOctree();
  layers: Layers = new Layers();
  scene: Object3D = null!;
  collider: Collider = null!;

  get bounds(): Box3 {
    if (!this.collider.geometry.boundingBox) {
      this.collider.geometry.computeBoundingBox();
    }
    return this.collider.geometry.boundingBox!;
  }

  get sphere(): Sphere {
    if (!this.collider.geometry.boundingSphere) {
      this.collider.geometry.computeBoundingSphere();
    }
    return this.collider.geometry.boundingSphere!;
  }

  // TODO remove the exclamation marks and handle the case when
  // the navMesh is not set in scene
  get navMesh(): RecastNavMesh {
    return this.scene?.navMesh!.instance!;
  }

  get navMeshQuery(): NavMeshQuery {
    return this.scene?.navMesh!.navMeshQuery!;
  }

  constructor() {
    super();
    this.layers.set(DOLLHOUSE_LAYER_INTERACTIVE);
  }

  setScene(scene: Object3D) {
    this.collider = new Collider(scene);
    this.scene = scene;

    // calculate navmesh floor offset
    this.navMeshFloorOffset = navMeshDistanceFromObject(this.navMeshQuery, scene);

    const bounds = new Box3().setFromObject(scene);
    this.octree.setBounds(bounds);
    // octree internally does node update after a setTimeout
    // todo - update octree implementation to better handle ready state without timeout
    setTimeout(() => {
      this.emit('ready');
    }, 100);
  }

  add(object: Object3D) {
    this.objects.add(object);
    this.emit('update');
  }

  remove(object: Object3D) {
    this.objects.delete(object);
    this.emit('update');
  }
}

export default World;
